import { parseDay, type GoalProgress } from '@map-my-money/engine';
import { GOALS_HORIZON_YEARS } from '@map-my-money/views';
import { useCallback, useId, useState } from 'react';
import { ChatBanner, ResultStamp } from '../components/ChatLink.tsx';
import { DebtDates, GoalStatus } from '../components/cards.tsx';
import type { WireGoal } from '../api/types.ts';
import { AskAssistant } from '../components/Assistant.tsx';
import { Icon } from '../components/Icon.tsx';
import { PageHeader } from '../components/Shell.tsx';
import { Field, Modal, ProgressBar, Ring } from '../components/ui.tsx';
import { t, type MessageKey } from '../i18n/index.ts';
import { compactMoney, percent } from '../lib/format.ts';
import { useForecastView, useGoalsResult, useIsDesktop } from '../lib/hooks.ts';
import { goals as goalsResult } from '../lib/model.ts';
import { useActivePlan, usePlan, useStore } from '../state/store.tsx';
import { useDocumentTitle } from '../lib/documentTitle.ts';

const SERIES: readonly WireGoal['series'][] = ['netWorth', 'investment', 'cash', 'debt', 'passive'];
const TONE: Record<string, 'cyber' | 'violet' | 'mint' | 'terracotta'> = { netWorth: 'cyber', investment: 'violet', cash: 'mint', debt: 'terracotta', passive: 'mint' };

export function GoalsScreen() {
  useDocumentTitle(t('nav.goals'));
  const wire = usePlan();
  const desktop = useIsDesktop();
  const view = useForecastView(GOALS_HORIZON_YEARS);
  const result = useGoalsResult(GOALS_HORIZON_YEARS);
  const { waypoints } = useStore();
  const resultAt = useCallback((asOf: string) => goalsResult(wire, waypoints, parseDay(asOf), GOALS_HORIZON_YEARS), [wire, waypoints]);
  const [editing, setEditing] = useState<WireGoal | 'new' | null>(null);
  const { canEdit } = useActivePlan();
  if (!view || !result) return null;
  const goals = wire.goals ?? [];
  const progressFor = (id: string) => view.goals.find((g) => g.goal.id === id);
  const sorted = [...goals].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
  const add = (
    <button type="button" className={desktop ? 'button button--primary button--glow' : 'button button--sm button--primary'} disabled={!canEdit} onClick={() => setEditing('new')}>
      <Icon name="plus" size={16} strokeWidth={2.5} />
      {t('goals.add')}
    </button>
  );
  return (
    <div className={desktop ? 'page goals-page' : 'page page--mobile'}>
      {desktop ? (
        <PageHeader eyebrow={t('goals.eyebrow')} title={t('goals.title')} actions={add} />
      ) : (
        <header className="mobile-header"><h1 className="mobile-title">{t('goals.title')}</h1>{add}</header>
      )}
      <ChatBanner recompute={resultAt} />
      {sorted.length === 0 ? (
        <p className="card card--pad-lg muted">{t('goals.none')}</p>
      ) : (
        <ul className="goal-grid">
          {sorted.map((goal, i) => {
            const p = progressFor(goal.id);
            return <GoalCard key={goal.id} goal={goal} progress={p} northStar={i === 0} onEdit={() => setEditing(goal)} />;
          })}
        </ul>
      )}
      <DebtDates debtFree={result.debt_free_date} mortgageFree={result.mortgage_free_date} />
      <AskAssistant view="goals" />
      <ResultStamp result={result} />
      {editing && <GoalDialog goal={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function GoalCard({ goal, progress, northStar, onEdit }: { goal: WireGoal; progress: GoalProgress | undefined; northStar: boolean; onEdit: () => void }) {
  const { canEdit } = useActivePlan();
  const tone = TONE[goal.series] ?? 'cyber';
  return (
    <li className="card card--pad-lg goal-card">
      <div className="goal-card__head">
        <span className={`goal-list__icon goal-list__icon--${tone}`} aria-hidden="true"><Icon name={goal.series === 'debt' ? 'card' : goal.series === 'investment' ? 'bars' : 'goals'} size={16} /></span>
        <span className="goal-card__titles">
          <span className="goal-card__label">{goal.label ?? goal.id}</span>
          <span className="goal-card__meta">{progress && <GoalStatus goal={progress} />}</span>
          {northStar && <span className="goal-card__badge">{t('goals.northStar')}</span>}
        </span>
        <Ring percent={progress?.progressPercent ?? 0} tone={tone} size={44} />
      </div>
      {progress && (
        <>
          <ProgressBar percent={progress.progressPercent} label={t('goals.progress', { pct: Math.round(progress.progressPercent) })} />
          <div className="north-star__scale">
            <span>{compactMoney(progress.currentValue)}</span>
            <span className="strong">{percent(progress.progressPercent, { digits: 1 })}</span>
            <span>{compactMoney(progress.targetValue)}</span>
          </div>
        </>
      )}
      <button type="button" className="button button--sm button--outline goal-card__edit" disabled={!canEdit} onClick={onEdit} aria-label={`${t('goals.edit')}: ${goal.label ?? goal.id}`}>
        {t('goals.edit')}
      </button>
    </li>
  );
}

function GoalDialog({ goal, onClose }: { goal: WireGoal | null; onClose: () => void }) {
  const wire = usePlan();
  const { savePlan } = useStore();
  const [label, setLabel] = useState(goal?.label ?? '');
  const [series, setSeries] = useState<WireGoal['series']>(goal?.series ?? 'netWorth');
  const [operation, setOperation] = useState<WireGoal['operation']>(goal?.operation ?? 'crossover');
  const [target, setTarget] = useState(goal?.targetValue ?? '');
  const [by, setBy] = useState(goal?.targetDate ?? '');
  const [busy, setBusy] = useState(false);
  const id = useId();
  const targetNum = Number(target.replace(/[£$,\s]/g, ''));
  const valid = label.trim() !== '' && target.trim() !== '' && Number.isFinite(targetNum) && targetNum >= 0;
  const goals = wire.goals ?? [];
  const save = async () => {
    if (!valid) return;
    setBusy(true);
    const { targetDate: _previous, ...base } = goal ?? { id: `goal-${Date.now().toString(36)}`, priority: goals.length };
    const next: WireGoal = { ...base, label: label.trim(), series, operation, targetValue: String(targetNum), ...(by ? { targetDate: by } : {}) };
    const list = goal ? goals.map((g) => (g.id === goal.id ? next : g)) : [...goals, next];
    const ok = await savePlan({ ...wire, goals: list }, t(goal ? 'plan.summaryText.goalUpdated' : 'plan.summaryText.goalAdded', { name: next.label ?? '' }));
    setBusy(false);
    if (ok) onClose();
  };
  const remove = async () => {
    if (!goal) return;
    setBusy(true);
    const ok = await savePlan({ ...wire, goals: goals.filter((g) => g.id !== goal.id) }, t('plan.summaryText.goalRemoved', { name: goal.label ?? goal.id }));
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <Modal
      title={goal ? t('goals.edit') : t('goals.new')}
      onClose={onClose}
      footer={
        <>
          {goal && <button type="button" className="button button--ghost" disabled={busy} onClick={() => void remove()}>{t('common.delete')}</button>}
          <button type="button" className="button button--primary" disabled={!valid || busy} onClick={() => void save()}>{t('common.save')}</button>
        </>
      }
    >
      <Field label={t('goals.form.label')} htmlFor={`${id}-label`}>
        <input id={`${id}-label`} className="input" value={label} onChange={(e) => setLabel(e.target.value)} />
      </Field>
      <div className="field-row">
        <Field label={t('goals.form.series')} htmlFor={`${id}-series`}>
          <select id={`${id}-series`} className="input" value={series} onChange={(e) => setSeries(e.target.value as WireGoal['series'])}>
            {SERIES.map((s) => <option key={s} value={s}>{t(`goals.series.${s}` as MessageKey)}</option>)}
          </select>
        </Field>
        <Field label={t('goals.form.operation')} htmlFor={`${id}-op`}>
          <select id={`${id}-op`} className="input" value={operation} onChange={(e) => setOperation(e.target.value as WireGoal['operation'])}>
            <option value="crossover">{t('goals.form.reachAtLeast')}</option>
            <option value="crossunder">{t('goals.form.fallTo')}</option>
          </select>
        </Field>
      </div>
      <Field label={t('goals.form.target')} htmlFor={`${id}-target`} error={target.trim() !== '' && !Number.isFinite(targetNum) ? t('plan.field.number') : null}>
        <input id={`${id}-target`} className="input" inputMode="decimal" value={target} onChange={(e) => setTarget(e.target.value)} />
      </Field>
      <Field label={t('goals.by.label')} htmlFor={`${id}-by`} hint={t('goals.by.hint')}>
        <span className="goal-by">
          <input id={`${id}-by`} className="input" type="date" value={by} onChange={(e) => setBy(e.target.value)} />
          {by && <button type="button" className="link-button" onClick={() => setBy('')}>{t('goals.by.clear')}</button>}
        </span>
      </Field>
    </Modal>
  );
}
